// CreateProfile.jsx
//
// Pick the Kleido Spaces you want under one user ID. Drag from the left
// box to the right, or tap. Five maximum.

import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./CreateProfile.css";
import kleidologo from "../../../../assets/images/Kleido.png";

/* ─────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────── */

/** the space types a user can create — edit this list to change what is offered */
const PROFILE_TYPES = [
  "Drama",
  "Music",
  "Speaker",
  "Food",
  "Sports",
  "Decor",
  "Fashion",
  "Dance",
  "Travel",
  "Biking",
  "Hiking",
];

/** hard ceiling on spaces per user */
const MAX_SPACES = 5;

/* ─────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────── */

const CreateProfile = () => {
  const navigate = useNavigate();

  const [userId, setUserId] = useState("");
  const [selected, setSelected] = useState([]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [dragged, setDragged] = useState(null);

  const available = useMemo(
    () => PROFILE_TYPES.filter((t) => !selected.includes(t)),
    [selected],
  );

  const atLimit = selected.length >= MAX_SPACES;

  /* ── add / remove ─────────────────────────────────────── */

  const addType = (type) => {
    setSelected((prev) => {
      if (prev.includes(type)) return prev;
      if (prev.length >= MAX_SPACES) {
        setError(`You can create up to ${MAX_SPACES} spaces.`);
        window.setTimeout(() => setError(""), 3000);
        return prev;
      }
      setError("");
      return [...prev, type];
    });
  };

  const removeType = (type) => {
    setSelected((prev) => prev.filter((t) => t !== type));
    setError("");
  };

  /* ── drag and drop ────────────────────────────────────── */

  const handleDrop = (e) => {
    e.preventDefault();
    if (dragged) addType(dragged);
    setDragged(null);
  };

  /* ── submit ───────────────────────────────────────────── */

  const handleSubmit = (e) => {
    e.preventDefault();

    const trimmedId = userId.trim();

    if (!trimmedId) {
      setError("Enter your user ID.");
      return;
    }
    if (selected.length === 0) {
      setError("Pick at least one space to create.");
      return;
    }
    if (selected.length > MAX_SPACES) {
      setError(`You can create up to ${MAX_SPACES} spaces.`);
      return;
    }

    setError("");

    const created = selected.map((profileType) => ({
      userId: trimmedId,
      profileType,
      profileName: `${trimmedId}_${profileType}`,
      value: profileType,
      label: `${trimmedId}_${profileType}`,
    }));

    try {
      const raw = localStorage.getItem("profiles");
      const parsed = raw ? JSON.parse(raw) : [];
      const existing = Array.isArray(parsed) ? parsed : [];

      // replace any space this user already has of the same type
      const merged = [
        ...existing.filter(
          (p) => !created.some((c) => c.userId === p.userId && c.profileType === p.profileType),
        ),
        ...created,
      ];

      localStorage.setItem("profiles", JSON.stringify(merged));
      // the status page needs to know whose spaces were just created
      localStorage.setItem("lastCreatedUserId", trimmedId);
    } catch (err) {
      console.error("Failed to store profiles:", err);
    }

    navigate(`/create-space-success/?userId=${encodeURIComponent(trimmedId)}`, {
      state: { userId: trimmedId },
    });
  };

  /* ── render ───────────────────────────────────────────── */

  return (
    <div className="cp-auth-container">
      <div className="pc-form-container" style={{background: "black" }}>
        <div className="form-box-header">
          <h2 style={{color: "white"}}>Create Kleido Spaces</h2>
          <img src={kleidologo} alt="Kleido" className="logo-default-container-img" style={{width: 100, height: 100}} />
        </div>

        {error && <p className="error-message">{error}</p>}

        <form onSubmit={handleSubmit}>
          {/* user id */}
          <div className="pc-form-group-row">
            <div className="pc-form-group">
              <label htmlFor="cp-userid" style={{color: "white"}}>
                <span className="pc-req" style={{color: "white"}}>*</span> User ID
              </label>
              <input
                id="cp-userid"
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Your Kleido user ID"
                required
              />
            </div>
          </div>

          {/* picker */}
          <div className="pc-form-group">
            <label style={{color: "white"}}>
              <span className="pc-req" style={{color: "white"}}>*</span> Spaces to create
              <span className="pc-hint" style={{color: "white"}}> — drag across or tap, up to {MAX_SPACES}</span>
            </label>

            <div className="cp-picker">
              {/* available */}
              <div className="cp-box">
                <div className="cp-box-head">
                  <h4>Available</h4>
                  <span className="cp-box-count">{available.length}</span>
                </div>
                <div className="cp-box-body">
                  {available.length === 0 ? (
                    <p className="cp-box-empty">All types selected.</p>
                  ) : (
                    available.map((type) => (
                      <div
                        key={type}
                        className={`cp-item ${atLimit ? "is-disabled" : ""}`}
                        draggable={!atLimit}
                        onDragStart={() => setDragged(type)}
                        onDragEnd={() => setDragged(null)}
                        onClick={() => addType(type)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") addType(type);
                        }}
                      >
                        {type}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* arrows */}
              <div className="cp-arrows" aria-hidden="true">
                <span>→</span>
                <span>←</span>
              </div>

              {/* selected */}
              <div
                className="cp-box cp-box-drop"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                <div className="cp-box-head">
                  <h4>Your spaces</h4>
                  <span className={`cp-box-count ${atLimit ? "is-full" : ""}`}>
                    {selected.length}/{MAX_SPACES}
                  </span>
                </div>
                <div className="cp-box-body">
                  {selected.length === 0 ? (
                    <p className="cp-box-empty">Drag a profile type here.</p>
                  ) : (
                    selected.map((type) => (
                      <div key={type} className="cp-item cp-item-selected">
                        <span>{type}</span>
                        <button
                          type="button"
                          className="cp-item-remove"
                          onClick={() => removeType(type)}
                          aria-label={`Remove ${type}`}
                        >
                          ✕
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {selected.length > 0 && userId.trim() && (
              <p className="pc-hint cp-preview">
                Will create: {selected.map((t) => `${userId.trim()}_${t}`).join(", ")}
              </p>
            )}
          </div>

          {/* terms */}
          <div className="pc-form-group-row">
            <div className="terms-conditions">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={() => setAcceptedTerms((p) => !p)}
              />
              <label htmlFor="terms">
                I accept the{" "}
                <Link to="/privacy-policy" target="_blank">Terms and Conditions</Link>
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="submit-btn"
            disabled={!acceptedTerms || selected.length === 0 || !userId.trim()}
          >
            Create {selected.length > 0 ? selected.length : ""}{" "}
            {selected.length === 1 ? "Space" : "Spaces"}
          </button>

          <div className="copyright-container">
            <p className="copyright">
              Kleido © InitiumNet Technology Pvt Ltd. For more on privacy policies and
              cookies, click <Link to="/">here</Link>.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProfile;